import { Component, inject, OnInit, Type } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SkeletonModule } from 'primeng/skeleton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CalendarModule } from 'primeng/calendar';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, Observable } from 'rxjs';
import { PortalTheme } from '@app/@shared/models';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { CredentialsService } from '@app/@shared/services/credentials.service';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { TimezoneService } from '@app/resources/services';
import { LoyaltyDashboardApiService } from './services/loyalty-dashboard-api.service';
import {
  CUSTOM_PERIOD,
  CUSTOM_PERIOD_OPTION,
  CustomPeriod,
  DASHBOARD_PERIOD_OPTIONS,
  DashboardPeriodDays,
  DEFAULT_DASHBOARD_PERIOD_DAYS,
  PeerVerticalNameWidget,
  WidgetSize,
} from './models/loyalty-dashboard.model';
import { resolveLayout } from './loyalty-dashboard.layout';

interface RenderedWidget {
  type: string;
  component: Type<unknown>;
  size: WidgetSize;
  status: 'loading' | 'loaded';
  placeholder: boolean;
  data: unknown;
}

@UntilDestroy()
@Component({
  selector: 'app-loyalty-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, SkeletonModule, SelectButtonModule, CalendarModule],
  templateUrl: './loyalty-dashboard.component.html',
  styleUrls: ['./loyalty-dashboard.component.scss'],
})
export class LoyaltyDashboardComponent implements OnInit {
  private readonly api = inject(LoyaltyDashboardApiService);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly credentialsService = inject(CredentialsService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly cssValidatorService = inject(CssValidatorService);

  private readonly layout = resolveLayout();

  protected readonly CUSTOM_PERIOD = CUSTOM_PERIOD;

  protected readonly isImpersonation = this.credentialsService.isImpersonation();

  protected readonly periodOptions = this.isImpersonation
    ? [...DASHBOARD_PERIOD_OPTIONS, CUSTOM_PERIOD_OPTION]
    : DASHBOARD_PERIOD_OPTIONS;

  protected selectedPeriod: DashboardPeriodDays | CustomPeriod = DEFAULT_DASHBOARD_PERIOD_DAYS;

  protected customFromDate: Date | null = null;
  protected customToDate: Date | null = null;

  protected readonly maxDate = new Date(this.timezoneService.moment().format('MM/DD/YYYY'));

  protected readonly merchantName$: Observable<string> = this.applicationProfileService.applicationProfile$.pipe(
    map((profile) => profile.Merchant?.Name ?? '')
  );

  protected readonly peerVerticalName$: Observable<string> = this.api.widgets$.pipe(
    map((states) => (states['peerVerticalName']?.data as PeerVerticalNameWidget | null)?.name ?? '')
  );

  private readonly renderedLayout = this.layout.filter((widget) => !widget.dataOnly);
  private readonly pageNumbers = [...new Set(this.renderedLayout.map((widget) => widget.page))].sort();

  protected readonly pages$: Observable<RenderedWidget[][]> = this.api.widgets$.pipe(
    map((states) =>
      this.pageNumbers.map((pageNo) =>
        this.renderedLayout
          .filter((widget) => widget.page === pageNo)
          .map((widget) => ({
            type: widget.type,
            component: widget.component,
            size: widget.size,
            status:
              widget.placeholder || states[widget.type]?.status === 'loaded'
                ? ('loaded' as const)
                : ('loading' as const),
            placeholder: widget.placeholder,
            data: widget.placeholder ? null : states[widget.type]?.data ?? null,
          }))
      )
    )
  );

  ngOnInit(): void {
    this.applicationProfileService.applicationProfile$
      .pipe(untilDestroyed(this))
      .subscribe((profile) => this.setCSSCustomProperties(profile.PortalTheme?.Theme));

    this.loadStandard(DEFAULT_DASHBOARD_PERIOD_DAYS);
  }

  protected onPeriodChange(value: DashboardPeriodDays | CustomPeriod): void {
    if (value === this.selectedPeriod) {
      return;
    }
    this.selectedPeriod = value;

    if (value === CUSTOM_PERIOD) {
      this.tryLoadCustom();
      return;
    }

    this.loadStandard(value);
  }

  protected onCustomFromDateChange(date: Date | null): void {
    this.customFromDate = date;
    this.tryLoadCustom();
  }

  protected onCustomToDateChange(date: Date | null): void {
    this.customToDate = date;
    this.tryLoadCustom();
  }

  private loadStandard(daysBack: number): void {
    const to = this.timezoneService.moment();
    const from = to.clone().subtract(daysBack, 'days');
    this.loadRange(from.toDate(), to.toDate());
  }

  private tryLoadCustom(): void {
    if (!this.customFromDate || !this.customToDate) {
      return;
    }
    const from = this.merchantDay(this.customFromDate).startOf('day');
    const to = this.merchantDay(this.customToDate).endOf('day');
    if (from.isAfter(to)) {
      return;
    }
    this.loadRange(from.toDate(), to.toDate());
  }

  private merchantDay(date: Date): ReturnType<TimezoneService['moment']> {
    return this.timezoneService.moment().date(1).year(date.getFullYear()).month(date.getMonth()).date(date.getDate());
  }

  private loadRange(from: Date, to: Date): void {
    const requestedWidgets = this.layout.filter((widget) => !widget.placeholder).map((widget) => widget.type);
    this.api.load(requestedWidgets, from, to).pipe(untilDestroyed(this)).subscribe();
  }

  private setCSSCustomProperties(theme: PortalTheme | undefined): void {
    if (!theme) {
      return;
    }

    const root = document.documentElement.style;
    root.setProperty('--primary', theme.primary);
    root.setProperty('--text--color-default', theme.defaultTextColor);
    root.setProperty('--dashboard-card-bg', theme.dashboard?.cardBackgroundColor);
    root.setProperty('--dashboard-header-bg', theme.dashboard?.headerBackgroundColor);
    root.setProperty('--dashboard-header-text-color', theme.dashboard?.headerTextColor);
    root.setProperty('--dashboard-on-primary-text', theme.dashboard?.coloredCardTextColor);
    root.setProperty('--dashboard-trend-up', theme.dashboard?.loyalty);
    root.setProperty('--dashboard-trend-down', theme.table?.statusColors?.danger);

    // Check for undefined CSS variables
    this.cssValidatorService.checkUndefinedCssVariables('LoyaltyDashboardComponent');
  }
}
